import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Stack, Typography } from '@mui/material';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { LinkedinAccountStatuses } from './linkedin-accounts.types';

interface Props {
  status: LinkedinAccountStatuses;
  syncLoading: boolean;
  logoutLoading: boolean;

  handleOpenAppSyncAndSynchronizeLiAccount(): void;

  handleOpenAppSyncAndCorrectAccessData(): void;

  handleLogoutLinkedinAccount(): void;

  /** Soft cancel of a stuck login: stops the job and resets the status — never logs the device out. */
  handleCancelLinkedinLogin(): void;

  handleOpenAppSyncAndVerifyPin(): void;

  handleOpenAppSyncAndSolveCaptcha(): void;

  handleOpenAppSyncThirdPartyWebsite(): void;

  handleOpenRecruiterParallelLoginDialog(): void;

  handleOpenOnboardingIncompleteDialog(): void;

  handleOpenFairUsePauseDialog(): void;

  handleOpenTwoFaNotEnabledDialog(): void;
}

const disconnected = [LinkedinAccountStatuses.NOT_SYNCHRONIZED];
const connected = [LinkedinAccountStatuses.SYNCHRONIZED];
const connecting = [LinkedinAccountStatuses.PENDING];
const maintenanceRequired = [
  LinkedinAccountStatuses.TWO_FA,
  LinkedinAccountStatuses.WRONG_PIN,
  LinkedinAccountStatuses.WRONG_CREDENTIALS,
  LinkedinAccountStatuses.CAPTCHA,
  LinkedinAccountStatuses.TIMED_OUT_CAPTCHA,
  LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN,
  LinkedinAccountStatuses.ONBOARDING_INCOMPLETE,
  LinkedinAccountStatuses.CHOOSE_CONTRACT,
  LinkedinAccountStatuses.THIRD_PARTY,
  LinkedinAccountStatuses.THIRD_PARTY_TEXT,
  LinkedinAccountStatuses.TWO_FA_NOT_ENABLED,
];
/**
 * States a running login leaves behind for the operator. If that login is gone (deploy,
 * timeout), the action link leads nowhere — a screenshot click with no job to take it,
 * a contract pick nobody waits for. Always offer the soft reset next to it
 * (Steven, SCHWABEO 16.09.2026: „Drittanbieter Anmeldung", nothing clickable).
 */
const resettable = [
  LinkedinAccountStatuses.THIRD_PARTY,
  LinkedinAccountStatuses.THIRD_PARTY_TEXT,
  LinkedinAccountStatuses.CHOOSE_CONTRACT,
  LinkedinAccountStatuses.ONBOARDING_INCOMPLETE,
  LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN,
];
const temporarilyRestricted = [LinkedinAccountStatuses.FAIR_USE_PAUSE];
/** Requires operator manual re-authentication — automation is fully halted. */
const needsManualLogin = [LinkedinAccountStatuses.NEEDS_MANUAL_LOGIN];

const LinkedinAccountStatus: React.FC<Props> = ({
  status,
  syncLoading,
  logoutLoading,
  handleOpenAppSyncAndSynchronizeLiAccount,
  handleOpenAppSyncAndCorrectAccessData,
  handleLogoutLinkedinAccount,
  handleCancelLinkedinLogin,
  handleOpenAppSyncAndVerifyPin,
  handleOpenAppSyncAndSolveCaptcha,
  handleOpenAppSyncThirdPartyWebsite,
  handleOpenRecruiterParallelLoginDialog,
  handleOpenOnboardingIncompleteDialog,
  handleOpenFairUsePauseDialog,
  handleOpenTwoFaNotEnabledDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const loading = syncLoading || logoutLoading;

  return (
    <Stack gap={0.5}>
      <div
        className={cx(
          classes.status,
          needsManualLogin.includes(status)
            ? 'manualLoginRequired'
            : status === LinkedinAccountStatuses.TWO_FA_TIMED_OUT
            ? 'disconnected'
            : disconnected.includes(status)
            ? 'disconnected'
            : connected.includes(status)
            ? 'connected'
            : connecting.includes(status)
            ? 'connecting'
            : maintenanceRequired.includes(status)
            ? 'maintenance'
            : temporarilyRestricted.includes(status)
            ? 'restricted'
            : undefined
        )}
      >
        <Typography className={classes.statusText}>
          {needsManualLogin.includes(status)
            ? t('accountNeedsManualLogin')
            : status === LinkedinAccountStatuses.TWO_FA_TIMED_OUT
            ? t('twoFaTimedOut')
            : disconnected.includes(status)
            ? t('accountLoggedOut')
            : connected.includes(status)
            ? t('accountLoggedIn')
            : connecting.includes(status)
            ? t('accountConnecting')
            : maintenanceRequired.includes(status)
            ? t('accountAttentionRequired')
            : temporarilyRestricted.includes(status)
            ? t('accountTemporarilyRestricted')
            : status}
        </Typography>
      </div>
      {status !== LinkedinAccountStatuses.PENDING ? (
        <Stack gap={0.25}>
          <PaiLink
            text={
              status === LinkedinAccountStatuses.NEEDS_MANUAL_LOGIN
                ? t('signInAction')
                : status === LinkedinAccountStatuses.SYNCHRONIZED
                ? t('signOutAction')
                : status === LinkedinAccountStatuses.NOT_SYNCHRONIZED
                ? t('signInAction')
                : status === LinkedinAccountStatuses.TWO_FA_TIMED_OUT
                ? t('signInAction')
                : status === LinkedinAccountStatuses.TWO_FA
                ? t('enter2fa')
                : status === LinkedinAccountStatuses.WRONG_CREDENTIALS
                ? t('correctAccessData')
                : status === LinkedinAccountStatuses.TIMED_OUT_CAPTCHA
                ? t('requestNewCaptcha')
                : status === LinkedinAccountStatuses.WRONG_PIN
                ? t('requestNewCode')
                : status === LinkedinAccountStatuses.CAPTCHA
                ? t('solveCaptcha')
                : status === LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN
                ? t('parallelLogin')
                : status === LinkedinAccountStatuses.CHOOSE_CONTRACT
                ? t('chooseContract')
                : status === LinkedinAccountStatuses.ONBOARDING_INCOMPLETE
                ? t('incompleteOnboarding')
                : status === LinkedinAccountStatuses.FAIR_USE_PAUSE
                ? t('fairUsePause')
                : status === LinkedinAccountStatuses.THIRD_PARTY || status === LinkedinAccountStatuses.THIRD_PARTY_TEXT
                ? t('thirdPartyLogin')
                : status === LinkedinAccountStatuses.TWO_FA_NOT_ENABLED
                ? t('twoFaNotEnabled')
                : status
            }
            ml={'4px'}
            size={'lg'}
            variant={maintenanceRequired.includes(status) || needsManualLogin.includes(status) ? 'primary' : 'default'}
            IconComponent={maintenanceRequired.includes(status) || needsManualLogin.includes(status) ? <ArrowOutwardIcon /> : undefined}
            iconPlacement={'end'}
            action={
              status === LinkedinAccountStatuses.NEEDS_MANUAL_LOGIN
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.SYNCHRONIZED
                ? handleLogoutLinkedinAccount
                : status === LinkedinAccountStatuses.NOT_SYNCHRONIZED
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.TWO_FA_TIMED_OUT
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.TWO_FA
                ? handleOpenAppSyncAndVerifyPin
                : status === LinkedinAccountStatuses.WRONG_CREDENTIALS
                ? handleOpenAppSyncAndCorrectAccessData
                : status === LinkedinAccountStatuses.TIMED_OUT_CAPTCHA
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.WRONG_PIN
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.CAPTCHA
                ? handleOpenAppSyncAndSolveCaptcha
                : status === LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN
                ? handleOpenRecruiterParallelLoginDialog
                : status === LinkedinAccountStatuses.CHOOSE_CONTRACT
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.ONBOARDING_INCOMPLETE
                ? handleOpenOnboardingIncompleteDialog
                : status === LinkedinAccountStatuses.FAIR_USE_PAUSE
                ? handleOpenFairUsePauseDialog
                : status === LinkedinAccountStatuses.THIRD_PARTY || status === LinkedinAccountStatuses.THIRD_PARTY_TEXT
                ? handleOpenAppSyncThirdPartyWebsite
                : status === LinkedinAccountStatuses.TWO_FA_NOT_ENABLED
                ? handleOpenTwoFaNotEnabledDialog
                : undefined
            }
            disabled={loading}
          />
          {resettable.includes(status) && (
            <PaiLink
              text={t('cancelAndResetAccount')}
              ml={'4px'}
              size={'lg'}
              variant={'default'}
              // Soft reset (POST /:id/reset), never the logout — see the connecting branch below.
              action={handleCancelLinkedinLogin}
              disabled={loading}
            />
          )}
          {status === LinkedinAccountStatuses.SYNCHRONIZED && (
            // Re-login without logging out. "Abmelden" runs pm clear on the device and wipes
            // the app's device trust (next login needs 2FA for every app). A fresh login is
            // often needed while still "connected" — e.g. the Sales Navigator seat died but
            // consumer LinkedIn still answers, so nothing flips the status to logged-out. The
            // /sync endpoint queues a login on any status and never touches the cookie jar
            // (Toni Künzl 2026-09-17: the only way to re-login was a manual DB reset first).
            <PaiLink
              text={t('reLoginAction', 'Neu anmelden')}
              ml={'4px'}
              size={'lg'}
              variant={'default'}
              action={handleOpenAppSyncAndSynchronizeLiAccount}
              disabled={loading}
            />
          )}
        </Stack>
      ) : (
        // Connecting is not a dead-end: the sync window can auto-close (e.g. after a 2FA
        // timeout) and leave the row spinning with nothing to click. Always offer a way
        // to reopen the login/2FA window and a hard escape back to logged-out, so the
        // account can never get stuck in a connecting loop (Toni Künzl 2026-09-04).
        <Stack gap={0.25}>
          <Typography className={classes.text} ml={'4px'}>
            {t('loggingIn')}
          </Typography>
          <PaiLink
            text={t('reopenLoginWindow')}
            ml={'4px'}
            size={'lg'}
            variant={'default'}
            IconComponent={<ArrowOutwardIcon />}
            iconPlacement={'end'}
            action={handleOpenAppSyncAndSynchronizeLiAccount}
            disabled={loading}
          />
          <PaiLink
            text={t('cancelConnecting')}
            ml={'4px'}
            size={'lg'}
            variant={'default'}
            // A SOFT cancel (POST /:id/reset): stops the job and resets the status. It must
            // NOT be the logout — that runs `pm clear` on the device, wipes the app's cookie
            // jar and with it the device trust, so the next login needs credentials + 2FA
            // again for every app (Toni Künzl 2026-09-08: one "Abbrechen" → six failed
            // re-logins on a black sign-in form → manual-intervention lock).
            action={handleCancelLinkedinLogin}
            disabled={loading}
          />
        </Stack>
      )}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  status: {
    borderRadius: '16px',
    width: 'fit-content',
    padding: '0 12px',
    '&.connected': {
      border: `1px solid ${theme.palette.backgrounds.success}`,
      background: theme.palette.backgrounds.lightGreen,
    },
    '&.connecting': {
      border: `1px solid ${theme.palette.primary.main}`,
      background: theme.palette.backgrounds.lightViolet,
    },
    '&.disconnected': {
      border: `1px solid ${theme.palette.greys.light}`,
      background: theme.palette.greys.lightest,
    },
    '&.maintenance': {
      border: `1px solid ${theme.palette.backgrounds.warning}`,
      background: theme.palette.backgrounds.lightOrange,
    },
    '&.restricted': {
      border: `1px solid ${theme.palette.greys.light}`,
      background: theme.palette.greys.lightest,
    },
    '&.manualLoginRequired': {
      border: `1px solid ${theme.palette.error.light}`,
      background: theme.palette.backgrounds.lightRed,
    },
  },
  statusText: {
    fontSize: theme.typography.pxToRem(15),
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default LinkedinAccountStatus;
